/**
 * @file: sentry.ts
 * @description sentry integration code for web
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */
import { config } from '@euler/config';
import type { UserIdentity } from '@euler/model';
import { NavigationContainerRef } from '@react-navigation/core';
import { BrowserTracing } from '@sentry/browser';
import { CaptureConsole, ExtraErrorData } from '@sentry/integrations';
import * as Sentry from '@sentry/react';
import { Breadcrumb, CaptureContext, Event, Scope } from '@sentry/types';
import { Fragment } from 'react';
import { Platform } from 'react-native';
import { getSentryDistribution, getSentryRelease } from './sentry.shared';

const debug = require('debug')('integration:sentry');

export const { withErrorBoundary } = Sentry;
export const TouchEventBoundary = Fragment;

export namespace sentry {
  export function identify(user: UserIdentity) {
    if (!config.sentry.enabled) return;
    const { uid, userName, email, ...extra } = user;
    Sentry.setUser({
      id: uid,
      username: userName,
      email,
      ...extra,
    });
  }

  export function reset() {
    if (!config.sentry.enabled) return;
    Sentry.setUser(null);
  }

  export function setup() {
    if (!config.sentry.enabled) return;
    // see https://docs.sentry.io/platforms/react-native/configuration/options/
    // for more common options that are available
    Sentry.init({
      dsn: config.sentry.dsn,
      environment: config.environment,
      release: getSentryRelease(),
      dist: getSentryDistribution(),
      sampleRate: 1.0, // this is also the default, just make it more explicit
      tracesSampleRate: config.sentry.tracesSampleRate,
      debug: false,
      normalizeDepth: 4, // default is 3
      integrations: [
        new ExtraErrorData() as any,
        new BrowserTracing() as any,
        new CaptureConsole({ levels: ['warn', 'error'] }),
      ],
      ignoreErrors: [
        'Animated: `useNativeDriver` is not supported because the native animated module is missing.',
      ],
      beforeSend: (event, _hint) => {
        // can change the event fingerprint here as documented below:
        // https://docs.sentry.io/platforms/react-native/usage/sdk-fingerprinting/
        return event;
      },
    });
    Sentry.setTag('os', Platform.OS);
  }

  export function registerRoutingInstrumentation(
    navigator: NavigationContainerRef<any>,
  ) {
    if (!config.sentry.enabled) return;
    debug('register sentry routing instrumentation: ', navigator);
  }

  export function addBreadcrumb(breadcrumb: Breadcrumb) {
    if (!config.sentry.enabled) return;
    Sentry.addBreadcrumb(breadcrumb);
  }

  export function configureScope(callback: (scope: Scope) => void) {
    if (!config.sentry.enabled) return;
    Sentry.configureScope(callback as any);
  }

  export function withScope(callback: (scope: Scope) => void) {
    if (!config.sentry.enabled) return;
    Sentry.withScope(callback as any);
  }

  export function captureException(exception: any, context?: CaptureContext) {
    if (!config.sentry.enabled) return;
    Sentry.captureException(exception, context as any);
  }

  export function captureMessage(message: string, context?: CaptureContext) {
    if (!config.sentry.enabled) return;
    Sentry.captureMessage(message, context as any);
  }

  export function captureEvent(event: Event): string {
    if (!config.sentry.enabled) return '';
    return Sentry.captureEvent(event as any);
  }
}
